import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney, fmtDay } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { toast } from "sonner";
import { Plus, FileText, Pencil, Trash2, ArrowLeft, Receipt, ClipboardList } from "lucide-react";

const selCls = "h-10 rounded-lg border border-slate-200 px-3 text-sm bg-white";
const INV_COLORS = { "Émise": "bg-slate-100 text-slate-700", "Partiellement payée": "bg-amber-100 text-amber-700", "Payée": "bg-emerald-100 text-emerald-700" };
const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function ProClientDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { isAdmin, settings } = useAuth();
  const cur = settings?.currency || "KMF";

  const [client, setClient] = useState(null);
  const [services, setServices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState(null);

  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [fDept, setFDept] = useState("");
  const [fBilled, setFBilled] = useState("");
  const [ordPage, setOrdPage] = useState(1);
  const [invPage, setInvPage] = useState(1);

  const [ao, setAo] = useState(null); // add/edit order form or null
  const [gi, setGi] = useState(null); // generate invoice state or null
  const [edit, setEdit] = useState(null); // edit client form or null
  const [unbilled, setUnbilled] = useState([]);
  const [sel, setSel] = useState(new Set());

  const loadClient = useCallback(() => {
    api.get(`/pro/clients/${id}`).then((r) => setClient(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/pro/invoices?client_id=${id}`).then((r) => setInvoices(r.data)).catch(() => {});
    api.get(`/pro/stats?client_id=${id}`).then((r) => setStats(r.data)).catch(() => {});
  }, [id]);

  const loadOrders = useCallback(() => {
    const p = { client_id: id };
    if (fFrom) p.date_from = fFrom;
    if (fTo) p.date_to = fTo;
    if (fDept) p.department = fDept;
    if (fBilled) p.billed = fBilled;
    api.get("/pro/orders", { params: p }).then((r) => setOrders(r.data)).catch((e) => toast.error(apiError(e)));
  }, [id, fFrom, fTo, fDept, fBilled]);

  useEffect(() => { loadClient(); api.get("/services", { params: { active_only: true } }).then((r) => setServices(r.data)).catch(() => {}); }, [loadClient]);
  useEffect(() => { loadOrders(); }, [loadOrders]);
  useEffect(() => { setOrdPage(1); }, [fFrom, fTo, fDept, fBilled]);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(orders.length / PAGE_SIZE));
    if (ordPage > maxPage) setOrdPage(maxPage);
  }, [orders.length, ordPage]);

  const pagedOrders = orders.slice((ordPage - 1) * PAGE_SIZE, ordPage * PAGE_SIZE);
  const pagedInvoices = invoices.slice((invPage - 1) * PAGE_SIZE, invPage * PAGE_SIZE);
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
    if (invPage > maxPage) setInvPage(maxPage);
  }, [invoices.length, invPage]);

  const svcPrice = (sid) => services.find((s) => s.id === sid)?.price || 0;

  const saveOrder = async () => {
    if (!ao.service_id) return toast.error("Choisissez un service");
    if (!ao.date) return toast.error("Date requise");
    const body = { pro_client_id: id, date: ao.date, department: ao.department, service_id: ao.service_id, quantity_kg: Number(ao.quantity_kg) || 0, note: ao.note };
    try {
      if (ao.editId) await api.put(`/pro/orders/${ao.editId}`, body);
      else await api.post("/pro/orders", body);
      toast.success("Commande enregistrée"); setAo(null); loadOrders(); loadClient();
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeOrder = async (o) => {
    if (!window.confirm("Supprimer cette commande ?")) return;
    try { await api.delete(`/pro/orders/${o.id}`); toast.success("Supprimée"); loadOrders(); loadClient(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openGi = () => { setGi({ from: monthStart(), to: today(), obs: "" }); setUnbilled([]); setSel(new Set()); };
  const loadUnbilled = async (from, to) => {
    try {
      const { data } = await api.get("/pro/orders/unbilled", { params: { client_id: id, date_from: from, date_to: to } });
      setUnbilled(data); setSel(new Set(data.map((o) => o.id)));
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { if (gi?.from && gi?.to) loadUnbilled(gi.from, gi.to); /* eslint-disable-next-line */ }, [gi?.from, gi?.to]);

  const openEditClient = () => setEdit({ name: client.name, phone: client.phone || "", payment_terms: client.payment_terms || "", notes: client.notes || "" });
  const saveClient = async () => {
    if (!edit.name.trim()) return toast.error("Nom requis");
    try { await api.put(`/pro/clients/${id}`, edit); toast.success("Client mis à jour"); setEdit(null); loadClient(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleSel = (oid) => setSel((p) => { const n = new Set(p); n.has(oid) ? n.delete(oid) : n.add(oid); return n; });
  const selectedOrders = unbilled.filter((o) => sel.has(o.id));
  const selKg = selectedOrders.reduce((s, o) => s + (o.quantity_kg || 0), 0);
  const selAmount = selectedOrders.reduce((s, o) => s + (o.amount || 0), 0);

  const generateInvoice = async () => {
    if (sel.size === 0) return toast.error("Sélectionnez au moins une commande");
    try {
      const { data } = await api.post("/pro/invoices", { pro_client_id: id, order_ids: [...sel], period_from: gi.from, period_to: gi.to, observation: gi.obs });
      toast.success(`Facture ${data.number} générée`); setGi(null); loadClient(); loadOrders();
      nav(`/pro/invoices/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!client) return <div className="text-slate-400 p-8">Chargement...</div>;

  return (
    <div className="space-y-6" data-testid="pro-client-detail">
      <button onClick={() => nav("/pro")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft size={16} /> Retour aux professionnels</button>

      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-display font-extrabold text-slate-800">{client.name}</h1>
          <p className="text-slate-500 text-sm">{client.phone || "—"}{client.payment_terms ? ` · Règlement : ${client.payment_terms}` : ""}</p>
          {client.notes && <p className="text-xs text-slate-400 mt-1">{client.notes}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="statement-btn" onClick={() => nav(`/pro/clients/${id}/statement`)} variant="outline" className="border-slate-200 text-slate-700"><ClipboardList size={16} className="mr-1.5" /> Relevé de compte</Button>
          <Button data-testid="add-pro-order-btn" onClick={() => setAo({ date: today(), department: "", service_id: services[0]?.id || "", quantity_kg: "", note: "" })} className="bg-sky-600 hover:bg-sky-700"><Plus size={16} className="mr-1.5" /> Ajouter une commande</Button>
          <Button data-testid="generate-invoice-btn" onClick={openGi} variant="outline" className="border-emerald-200 text-emerald-700"><FileText size={16} className="mr-1.5" /> Générer une facture</Button>
          {isAdmin && (
            <Button data-testid="edit-client-btn" onClick={openEditClient} variant="outline" className="border-slate-200 text-slate-700"><Pencil size={16} className="mr-1.5" /> Modifier le client</Button>
          )}
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="p-4"><p className="text-xs text-slate-500">Total kg</p><p className="text-lg font-bold mt-1">{stats.total_kg} kg</p></Card>
          <Card className="p-4"><p className="text-xs text-slate-500">Montant total</p><p className="text-lg font-bold mt-1">{fmtMoney(stats.total_amount, cur)}</p></Card>
          <Card className="p-4"><p className="text-xs text-slate-500">Encaissé</p><p className="text-lg font-bold text-emerald-600 mt-1">{fmtMoney(stats.collected, cur)}</p></Card>
          <Card className="p-4"><p className="text-xs text-slate-500">Reste à encaisser</p><p className="text-lg font-bold text-red-600 mt-1">{fmtMoney(stats.remaining, cur)}</p></Card>
        </div>
      )}

      {/* Commandes */}
      <div>
        <h2 className="font-semibold text-slate-700 mb-2">Commandes professionnelles</h2>
        <Card className="p-3 flex flex-wrap gap-2 items-end mb-3">
          <div><label className="text-xs text-slate-500 block">Du</label><input type="date" data-testid="ord-filter-from" className={selCls} value={fFrom} onChange={(e) => setFFrom(e.target.value)} /></div>
          <div><label className="text-xs text-slate-500 block">Au</label><input type="date" data-testid="ord-filter-to" className={selCls} value={fTo} onChange={(e) => setFTo(e.target.value)} /></div>
          <div><label className="text-xs text-slate-500 block">Service interne</label><Input data-testid="ord-filter-dept" className="h-10" placeholder="Ex: Réanimation" value={fDept} onChange={(e) => setFDept(e.target.value)} /></div>
          <div><label className="text-xs text-slate-500 block">Facturation</label>
            <select data-testid="ord-filter-billed" className={selCls} value={fBilled} onChange={(e) => setFBilled(e.target.value)}>
              <option value="">Toutes</option><option value="false">Non facturées</option><option value="true">Facturées</option>
            </select>
          </div>
        </Card>
        <div className="space-y-2">
          {pagedOrders.map((o) => (
            <Card key={o.id} data-testid={`pro-order-${o.id}`} className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium text-slate-800">{fmtDay(o.date)} · <span className="text-slate-600">{o.department || "—"}</span></p>
                <p className="text-sm text-slate-500">{o.service_name} · {o.quantity_kg} kg × {fmtMoney(o.unit_price, cur)}</p>
                {o.note && <p className="text-xs text-slate-400">{o.note}</p>}
              </div>
              <div className="flex items-center gap-3">
                <p className="font-semibold text-slate-800">{fmtMoney(o.amount, cur)}</p>
                {o.invoice_number ? (
                  <span className="text-xs bg-sky-100 text-sky-700 px-2 py-1 rounded-full whitespace-nowrap">{o.invoice_number}</span>
                ) : (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" data-testid={`edit-pro-order-${o.id}`} onClick={() => setAo({ editId: o.id, date: o.date, department: o.department, service_id: o.service_id, quantity_kg: o.quantity_kg, note: o.note })}><Pencil size={14} className="mr-1.5" /> Modifier</Button>
                    {isAdmin && <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" data-testid={`delete-pro-order-${o.id}`} onClick={() => removeOrder(o)}><Trash2 size={14} className="mr-1.5" /> Supprimer</Button>}
                  </div>
                )}
              </div>
            </Card>
          ))}
          {orders.length === 0 && <p className="text-slate-400 text-center py-6">Aucune commande</p>}
        </div>
        <Pagination page={ordPage} total={orders.length} onPage={setOrdPage} />
      </div>

      {/* Factures */}
      <div>
        <h2 className="font-semibold text-slate-700 mb-2">Factures</h2>
        <div className="space-y-2">
          {pagedInvoices.map((inv) => (
            <Card key={inv.id} data-testid={`pro-invoice-${inv.id}`} className="p-3 flex items-center justify-between gap-3 flex-wrap">
              <div>
                <p className="font-medium text-slate-800">{inv.number} <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${INV_COLORS[inv.status] || "bg-slate-100"}`}>{inv.status}</span></p>
                <p className="text-sm text-slate-500">Période {fmtDay(inv.period_from)} → {fmtDay(inv.period_to)} · {inv.total_kg} kg</p>
                <p className="text-xs text-slate-400">Total {fmtMoney(inv.total_amount, cur)} · Payé {fmtMoney(inv.amount_paid, cur)} · Reste {fmtMoney(inv.balance, cur)}</p>
              </div>
              <Button size="sm" variant="outline" data-testid={`open-pro-invoice-${inv.id}`} onClick={() => nav(`/pro/invoices/${inv.id}`)}><Receipt size={15} className="mr-1.5" /> Consulter</Button>
            </Card>
          ))}
          {invoices.length === 0 && <p className="text-slate-400 text-center py-6">Aucune facture</p>}
        </div>
        <Pagination page={invPage} total={invoices.length} onPage={setInvPage} />
      </div>

      {/* Dialog édition client */}
      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent data-testid="edit-client-dialog">
          <DialogHeader><DialogTitle>Modifier le client</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><label className="text-sm text-slate-600">Nom de l'établissement *</label><Input data-testid="ec-name" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600">Téléphone</label><Input data-testid="ec-phone" value={edit.phone} onChange={(e) => setEdit({ ...edit, phone: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600">Conditions de règlement</label><Input data-testid="ec-terms" value={edit.payment_terms} onChange={(e) => setEdit({ ...edit, payment_terms: e.target.value })} placeholder="Ex: 10 jours dès réception" /></div>
              <div><label className="text-sm text-slate-600">Observations (facultatif)</label><Input data-testid="ec-notes" value={edit.notes} onChange={(e) => setEdit({ ...edit, notes: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setEdit(null)}>Annuler</Button><Button data-testid="save-client" onClick={saveClient} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog ajout/édition commande */}
      <Dialog open={!!ao} onOpenChange={(v) => !v && setAo(null)}>
        <DialogContent data-testid="pro-order-dialog">
          <DialogHeader><DialogTitle>{ao?.editId ? "Modifier" : "Ajouter"} une commande</DialogTitle></DialogHeader>
          {ao && (
            <div className="space-y-3">
              <div><label className="text-sm text-slate-600">Date</label><input type="date" data-testid="ao-date" className={selCls + " w-full"} value={ao.date} onChange={(e) => setAo({ ...ao, date: e.target.value })} /></div>
              <div><label className="text-sm text-slate-600">Service interne / département</label><Input data-testid="ao-dept" value={ao.department} onChange={(e) => setAo({ ...ao, department: e.target.value })} placeholder="Ex: Réanimation" /></div>
              <div><label className="text-sm text-slate-600">Service WuraPro</label>
                <select data-testid="ao-service" className={selCls + " w-full"} value={ao.service_id} onChange={(e) => setAo({ ...ao, service_id: e.target.value })}>
                  <option value="">— Choisir —</option>
                  {services.map((s) => <option key={s.id} value={s.id}>{s.name} ({fmtMoney(s.price, cur)}{s.pricing_type === "per_kg" ? "/kg" : ""})</option>)}
                </select>
              </div>
              <div><label className="text-sm text-slate-600">Quantité (kg)</label><Input type="number" data-testid="ao-qty" value={ao.quantity_kg} onChange={(e) => setAo({ ...ao, quantity_kg: e.target.value })} /></div>
              <div className="bg-sky-50 rounded-lg p-3 text-sm flex justify-between"><span className="text-slate-600">Prix unitaire : {fmtMoney(svcPrice(ao.service_id), cur)}</span><span className="font-bold text-sky-700" data-testid="ao-amount">Montant : {fmtMoney(svcPrice(ao.service_id) * (Number(ao.quantity_kg) || 0), cur)}</span></div>
              <div><label className="text-sm text-slate-600">Observation (facultatif)</label><Input data-testid="ao-note" value={ao.note} onChange={(e) => setAo({ ...ao, note: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAo(null)}>Annuler</Button><Button data-testid="save-pro-order" onClick={saveOrder} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog génération facture */}
      <Dialog open={!!gi} onOpenChange={(v) => !v && setGi(null)}>
        <DialogContent data-testid="pro-generate-invoice-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Générer une facture</DialogTitle></DialogHeader>
          {gi && (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1"><label className="text-sm text-slate-600">Date début</label><input type="date" data-testid="gi-from" className={selCls + " w-full"} value={gi.from} onChange={(e) => setGi({ ...gi, from: e.target.value })} /></div>
                <div className="flex-1"><label className="text-sm text-slate-600">Date fin</label><input type="date" data-testid="gi-to" className={selCls + " w-full"} value={gi.to} onChange={(e) => setGi({ ...gi, to: e.target.value })} /></div>
              </div>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y">
                {unbilled.map((o) => (
                  <label key={o.id} data-testid={`gi-order-${o.id}`} className="flex items-center gap-2 p-2 text-sm cursor-pointer hover:bg-slate-50">
                    <input type="checkbox" className="h-4 w-4 accent-sky-600" checked={sel.has(o.id)} onChange={() => toggleSel(o.id)} />
                    <span className="flex-1">{fmtDay(o.date)} · {o.department || "—"} · {o.service_name} · {o.quantity_kg} kg</span>
                    <span className="font-medium">{fmtMoney(o.amount, cur)}</span>
                  </label>
                ))}
                {unbilled.length === 0 && <p className="text-slate-400 text-center py-4 text-sm">Aucune commande non facturée sur cette période</p>}
              </div>
              <div className="flex justify-between bg-emerald-50 rounded-lg p-3 text-sm font-semibold text-emerald-800" data-testid="gi-totals">
                <span>{selKg} kg</span><span>{fmtMoney(selAmount, cur)}</span>
              </div>
              <div><label className="text-sm text-slate-600">Observation (facultatif)</label><Input data-testid="gi-obs" value={gi.obs} onChange={(e) => setGi({ ...gi, obs: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setGi(null)}>Annuler</Button><Button data-testid="confirm-generate-invoice" onClick={generateInvoice} disabled={sel.size === 0} className="bg-emerald-600 hover:bg-emerald-700">Générer la facture ({sel.size})</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
